import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListCheck } from "@fortawesome/free-solid-svg-icons";
import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall.js";
import useDelayedLoading from "../../../hooks/useDelayedLoading";
import usePagination from "../../../hooks/usePagination.js";
import useSearchTerm from "../../../hooks/useSearchTerm.js";
import useDebounce from "../../../hooks/useDebounce.js";

import TaskDetailDrawer from "../../task/TaskDetailDrawer";
import TaskTable from "../../task/TaskTable";

import { useAppData } from "../../../context/AppDataContext";
import SearchInput from "../../input/SearchInput.jsx";
import Loading from "../../layout/Loading.jsx";
import Button from "../../core/Button.jsx";

const TaskListPage = () => {
  const history = useHistory();
  const queryParams = new URLSearchParams(history.location.search);
  const assigned_to_id = queryParams.get("assignedto");
  const [searchTerm, setSearchTerm] = useSearchTerm();
  const [selectedFilter, setSelectedFilter] = useState(() => {
    return (
      localStorage.getItem("myTasksFilter") ||
      (assigned_to_id === "me" ? "current" : "all")
    );
  });
  const [activeSprints, setActiveSprints] = useState([]);
  const sortStorageKey = assigned_to_id === "me" ? "myTasksSort" : "tasksSort";

  const [sortParams, setSortParams] = useState(() => {
    const storedSort = localStorage.getItem(sortStorageKey);

    if (queryParams.get("sort")) {
      return {
        sortBy: queryParams.get("sort"),
        sortDir: queryParams.get("sort_dir") || "asc",
      };
    }

    if (storedSort) {
      return JSON.parse(storedSort);
    }

    return {
      sortBy: "",
      sortDir: "asc",
    };
  });
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isFirstLoad = useRef(true);
  const { api } = useAppData() || {};
  const searchDebounce = useDebounce(searchTerm);

  const { fetchData, data, isLoading } = useTriggeredAPICall();
  const showLoading = useDelayedLoading(isLoading);

  const pageTitle = assigned_to_id === "me" ? "My Tasks" : "Tasks";
  const active = queryParams.get("active");
  const completed = queryParams.get("completed");
  const recentlyCompleted = queryParams.get("recentlyCompleted");
  const isSearchMode = Boolean(searchDebounce);
  const requestParams = new URLSearchParams();
  requestParams.set("include", "assignments,status,project,priority,tags");
  requestParams.set(
    "filter",
    selectedFilter === "recentlyCompleted"
      ? "recentlyCompleted"
      : selectedFilter
  );
  requestParams.set("sort", sortParams.sortBy);
  requestParams.set("sort_dir", sortParams.sortDir);
  if (isSearchMode) {
    requestParams.set("q", searchDebounce);
  }
  if (assigned_to_id) {
    requestParams.set("assignedto", assigned_to_id);
  }
  if (active) {
    requestParams.set("active", "true");
  }
  if (completed) {
    requestParams.set("completed", completed);
  }
  if (recentlyCompleted) {
    requestParams.set("recentlyCompleted", recentlyCompleted);
  }

  useEffect(() => {
    localStorage.setItem(sortStorageKey, JSON.stringify(sortParams));
  }, [sortParams, sortStorageKey]);

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);

    if (sortParams.sortBy) {
      params.set("sort", sortParams.sortBy);
      params.set("sort_dir", sortParams.sortDir);
    } else {
      params.delete("sort");
      params.delete("sort_dir");
    }

    history.replace({
      pathname: history.location.pathname,
      search: `?${params.toString()}`,
    });
  }, [sortParams]);

  const baseUrl = isSearchMode
    ? `/tasks/search?${requestParams.toString()}`
    : `/tasks?${requestParams.toString()}`;

  const { currentData, recordsData, listPage, getSavedPage } = usePagination(
    data,
    baseUrl,
    fetchData,
    "tasks"
  );

  useEffect(() => {
    const fetchActivSprints = async () => {
      const allActiveSprints = await api.sprint.getActiveSprints();
      setActiveSprints(allActiveSprints);
    };
    fetchActivSprints();
  }, []);

  const activeSprintIds = new Set(
    activeSprints.map((sprint) => sprint.sprint_id)
  );

  useEffect(() => {
    localStorage.setItem("myTasksFilter", selectedFilter);
  }, [selectedFilter]);

  useEffect(() => {
    if (isDrawerOpen) return;

    if (isFirstLoad.current) {
      isFirstLoad.current = false;

      listPage(getSavedPage());

      return;
    }

    listPage(1);
  }, [baseUrl, searchDebounce, assigned_to_id, active, completed]);

  useEffect(() => {
    if (currentData) {
      setTasks(currentData);
    }
  }, [currentData]);

  useEffect(() => {
    if (!isDrawerOpen) {
      listPage(recordsData.currentPage);
    }
  }, [isDrawerOpen]);

  const handleTasksChange = (updatedTasks) => {
    setTasks(updatedTasks);
  };

  const handleTaskClick = (taskId) => {
    setSelectedTaskId(taskId);
    setIsDrawerOpen(true);
  };

  const recentlyCompletedTasks = tasks.filter((task) => {
    const sprint = task.sprints[0];
    return (
      task.status === "Complete" &&
      sprint &&
      activeSprintIds.has(sprint.sprint_id)
    );
  });

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTaskId(null);
  };

  if (!api) {
    return <div className="loading">Initializing...</div>;
  }

  return (
    <div className="flex-align-start width-full task-list-page-wrapper min-width-9">
      <h1>
        <FontAwesomeIcon icon={faListCheck} className="my-tasks-icon" />{" "}
        {pageTitle}
      </h1>
      <div className="flex-space-between">
        <div className="flex-row-no-width">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="width-200px"
          />
          <div className="flex-start flex-space-between">
            <button
              className={`tab-button ${
                selectedFilter === "all" ? "selected" : ""
              }`}
              onClick={() => {
                setSelectedFilter("all");
              }}
            >
              All
            </button>

            <button
              className={`tab-button ${
                selectedFilter === "new" ? "selected" : ""
              }`}
              onClick={() => {
                setSelectedFilter("new");
              }}
            >
              New
            </button>

            <button
              className={`tab-button ${
                selectedFilter === "current" ? "selected" : ""
              }`}
              onClick={() => {
                setSelectedFilter("current");
              }}
            >
              Current Tasks
            </button>
            <button
              className={`tab-button ${
                selectedFilter === "qa" ? "selected" : ""
              }`}
              onClick={() => {
                setSelectedFilter("qa");
              }}
            >
              QA
            </button>
            <button
              className={`tab-button ${
                selectedFilter === "complete" ? "selected" : ""
              }`}
              onClick={() => {
                setSelectedFilter("complete");
              }}
            >
              Completed
            </button>
            {assigned_to_id === "me" && (
              <button
                className={`tab-button ${
                  selectedFilter === "recentlyCompleted" ? "selected" : ""
                }`}
                onClick={() => {
                  setSelectedFilter("recentlyCompleted");
                }}
              >
                Recently Completed
              </button>
            )}
          </div>
        </div>
        <Button to="/task-form">+ New Task</Button>
      </div>

      <div className="page-content">
        <TaskTable
          data={tasks}
          loading={showLoading}
          onDataChange={handleTasksChange}
          onTaskClick={handleTaskClick}
          showProjectColumn={true}
          onPageChange={listPage}
          recordsData={recordsData}
          baseUrl={baseUrl}
          setSortParams={setSortParams}
          sortParams={sortParams}
          task={tasks.task}
          version={false}
        />
      </div>

      <TaskDetailDrawer
        taskId={selectedTaskId}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        breadcrumbParent={{
          parentType: assigned_to_id === "me" ? "mytasks" : "tasks",
        }}
      />
    </div>
  );
};

export default TaskListPage;
